/**
 * MyRoyaltiesDialog (iter-34) — author-facing earnings summary.
 *
 * Surfaces 4 panes:
 *   - Top-line tiles: lifetime, this-month, pending payout, paid-to-date
 *   - Per-book breakdown (best-earner first)
 *   - 12-month line of monthly earnings
 *   - Recent sales table (last 50)
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { TrendingUp, BookOpen, Calendar, Wallet, Loader2, Sparkles, Store, Download, Zap, Clock, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { royaltiesApi } from '@/lib/api';
import { useCurrency } from '@/contexts/CurrencyContext';
import ItemisedRoyaltyPane from './ItemisedRoyaltyPane';   // Feb-2026 §2.6 itemisation

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

function StatTile({ label, value, sub, icon: Icon }) {
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-panel">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className="mt-1 text-[22px] font-semibold tracking-tight font-mono-stat">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function MonthlyBarChart({ data }) {
  const { format } = useCurrency();
  if (!data?.length) {
    return <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">No earnings recorded yet.</div>;
  }
  const max = Math.max(...data.map(d => d.royalty_cents), 1);
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-panel">
      <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Monthly earnings (last {data.length} months)</div>
      <div className="mt-3 flex items-end gap-1.5 h-32" data-testid="royalty-monthly-chart">
        {data.map((d) => {
          const h = Math.max(2, (d.royalty_cents / max) * 100);
          return (
            <div key={d.period} className="group relative flex flex-1 flex-col items-center">
              <div className="w-full rounded-t bg-primary/80 transition hover:bg-primary"
                   style={{ height: `${h}%` }} />
              <div className="mt-1 text-[9px] text-muted-foreground -rotate-45 origin-top whitespace-nowrap">
                {d.period}
              </div>
              <div className="pointer-events-none absolute bottom-full mb-1 hidden whitespace-nowrap rounded-sm bg-foreground px-1.5 py-0.5 text-[10px] text-background group-hover:block">
                {format(d.royalty_cents)} · {d.sales} sale{d.sales === 1 ? '' : 's'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TemplatesBreakdownPane({ data, format }) {
  if (!data) {
    return <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">Loading templates…</div>;
  }
  const s = data.summary || {};
  const top = data.top || [];
  const recent = data.recent || [];
  if ((s.lifetime_sales || 0) === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground" data-testid="royalty-templates-empty">
        <Sparkles className="mx-auto h-6 w-6 text-primary/60" />
        <p className="mt-2 font-semibold text-foreground">No template sales yet</p>
        <p className="mt-1">Publish a template to the marketplace and it&apos;ll appear here as soon as it earns.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <div className="rounded-md border border-border bg-card p-3 shadow-panel">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Store className="h-3.5 w-3.5" /> Template lifetime
          </div>
          <div className="mt-1 text-[20px] font-semibold font-mono-stat">{format(s.lifetime_cents)}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">{s.lifetime_sales} sales</div>
        </div>
        <div className="rounded-md border border-border bg-card p-3 shadow-panel">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" /> This month
          </div>
          <div className="mt-1 text-[20px] font-semibold font-mono-stat">{format(s.this_month_cents)}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">{s.this_month_sales} sales</div>
        </div>
        <div className="rounded-md border border-border bg-card p-3 shadow-panel">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Wallet className="h-3.5 w-3.5" /> Pending payout
          </div>
          <div className="mt-1 text-[20px] font-semibold font-mono-stat">{format(s.pending_payout_cents)}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">{s.pending_sales} awaiting</div>
        </div>
      </div>

      <div>
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5" /> Top-earning templates
        </div>
        <div className="overflow-hidden rounded-md border border-border">
          <Table className="text-[12px]">
            <TableHeader className="bg-panel-header/60 text-[11px] text-muted-foreground">
              <TableRow>
                <TableHead className="px-3 py-2 h-auto">Template</TableHead>
                <TableHead className="px-3 py-2 h-auto">Tier</TableHead>
                <TableHead className="px-3 py-2 h-auto text-right">Sales</TableHead>
                <TableHead className="px-3 py-2 h-auto text-right">Gross</TableHead>
                <TableHead className="px-3 py-2 h-auto text-right">Royalty</TableHead>
                <TableHead className="px-3 py-2 h-auto">Last sold</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top.map((t) => (
                <TableRow key={t.template_id} data-testid={`royalty-template-row-${t.template_id}`}>
                  <TableCell className="px-3 py-2 font-semibold">
                    {t.template_title}
                    {!t.still_listed && <span className="ml-1.5 text-[10px] text-muted-foreground">(unlisted)</span>}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-[11px] capitalize text-muted-foreground">{t.tier || '—'}</TableCell>
                  <TableCell className="px-3 py-2 text-right font-mono-stat">{t.sales}</TableCell>
                  <TableCell className="px-3 py-2 text-right font-mono-stat text-muted-foreground">{format(t.gross_cents)}</TableCell>
                  <TableCell className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{format(t.royalty_cents)}</TableCell>
                  <TableCell className="px-3 py-2 text-[11px] text-muted-foreground">
                    {t.last_sold_at ? new Date(t.last_sold_at).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {recent.length > 0 && (
        <details className="rounded-md border border-border">
          <summary className="cursor-pointer px-3 py-2 text-[11.5px] font-semibold text-muted-foreground">
            Recent template sales ({recent.length})
          </summary>
          <Table className="text-[12px]">
            <TableHeader className="bg-panel-header/60 text-[11px] text-muted-foreground">
              <TableRow>
                <TableHead className="px-3 py-2 h-auto">Sold</TableHead>
                <TableHead className="px-3 py-2 h-auto">Template</TableHead>
                <TableHead className="px-3 py-2 h-auto text-right">Gross</TableHead>
                <TableHead className="px-3 py-2 h-auto text-right">Royalty</TableHead>
                <TableHead className="px-3 py-2 h-auto">Payout</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="px-3 py-2 font-mono-stat">{r.sold_at ? new Date(r.sold_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell className="px-3 py-2">{r.book_title}</TableCell>
                  <TableCell className="px-3 py-2 text-right font-mono-stat text-muted-foreground">{format(r.gross_cents)}</TableCell>
                  <TableCell className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{format(r.royalty_cents)}</TableCell>
                  <TableCell className="px-3 py-2">
                    {r.payout_id
                      ? <span className="rounded-sm bg-emerald-100 text-emerald-900 px-1.5 py-0.5 text-[10.5px] font-semibold">paid</span>
                      : <span className="text-muted-foreground">pending</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </details>
      )}
    </div>
  );
}



// iter55g34 — Payouts ledger pane.
const STATUS_META = {
  paid_direct: { label: 'Paid instantly', icon: Zap,          cls: 'bg-emerald-100 text-emerald-900' },
  paid_manual: { label: 'Paid (manual)',  icon: CheckCircle2, cls: 'bg-sky-100 text-sky-900' },
  pending:     { label: 'Pending',        icon: Clock,        cls: 'bg-amber-100 text-amber-900' },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold ${meta.cls}`}
          data-testid={`payout-status-${status}`}>
      <Icon className="h-2.5 w-2.5" /> {meta.label}
    </span>
  );
}

function PayoutsPane({ data, format, statusFilter, setStatusFilter, csvUrl }) {
  if (!data) {
    return <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">Loading payouts…</div>;
  }
  const rows = data.rows || [];
  const totals = data.totals || {};
  const filtered = statusFilter === 'all' ? rows : rows.filter(r => r.status === statusFilter);
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return iso; }
  };
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground" data-testid="payouts-empty">
        <Wallet className="mx-auto h-6 w-6 text-primary/60" />
        <p className="mt-2 font-semibold text-foreground">No bookstore sales yet</p>
        <p className="mt-1">Once a reader buys one of your published books, the 85/15 revenue split will land here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="payouts-pane">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md border border-border bg-card p-3 shadow-panel">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Zap className="h-3.5 w-3.5" /> Paid instantly
          </div>
          <div className="mt-1 text-[20px] font-semibold font-mono-stat" data-testid="payouts-total-direct">
            {format(totals.paid_direct_cents || 0)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Stripe direct-split</div>
        </div>
        <div className="rounded-md border border-border bg-card p-3 shadow-panel">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" /> Paid (manual)
          </div>
          <div className="mt-1 text-[20px] font-semibold font-mono-stat" data-testid="payouts-total-manual">
            {format(totals.paid_manual_cents || 0)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Admin swept transfers</div>
        </div>
        <div className="rounded-md border border-border bg-card p-3 shadow-panel">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Clock className="h-3.5 w-3.5" /> Pending
          </div>
          <div className="mt-1 text-[20px] font-semibold font-mono-stat" data-testid="payouts-total-pending">
            {format(totals.pending_cents || 0)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Awaiting next sweep</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Filter</span>
        {['all', 'paid_direct', 'paid_manual', 'pending'].map((k) => (
          <button key={k}
                  type="button"
                  onClick={() => setStatusFilter(k)}
                  data-testid={`payout-filter-${k}`}
                  className={`rounded-sm border px-2 py-0.5 text-[11px] transition ${
                    statusFilter === k
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card hover:bg-panel-header/60'
                  }`}>
            {k === 'all' ? 'All' : STATUS_META[k].label}
          </button>
        ))}
        <a href={csvUrl}
           target="_blank" rel="noopener noreferrer"
           data-testid="payouts-csv-btn"
           className="ml-auto inline-flex items-center gap-1 rounded-sm border border-border bg-card px-2 py-0.5 text-[11px] hover:bg-panel-header/60">
          <Download className="h-3 w-3" /> CSV
        </a>
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        <Table className="text-[12px]">
          <TableHeader className="bg-panel-header/60 text-[11px] text-muted-foreground">
            <TableRow>
              <TableHead className="px-3 py-2 h-auto">Sold</TableHead>
              <TableHead className="px-3 py-2 h-auto">Book</TableHead>
              <TableHead className="px-3 py-2 h-auto text-right">Gross</TableHead>
              <TableHead className="px-3 py-2 h-auto text-right">Your share</TableHead>
              <TableHead className="px-3 py-2 h-auto">Status</TableHead>
              <TableHead className="px-3 py-2 h-auto">Reference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`payout-row-${r.id}`}>
                <TableCell className="px-3 py-2 font-mono-stat text-[11px]">{fmtDateTime(r.sold_at)}</TableCell>
                <TableCell className="px-3 py-2">{r.book_title}</TableCell>
                <TableCell className="px-3 py-2 text-right font-mono-stat text-muted-foreground">{format(r.gross_cents)}</TableCell>
                <TableCell className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{format(r.author_share_cents)}</TableCell>
                <TableCell className="px-3 py-2"><StatusPill status={r.status} /></TableCell>
                <TableCell
                  className="px-3 py-2 font-mono-stat text-[10.5px] text-muted-foreground truncate max-w-[10rem]"
                  title={r.payout_reference || ''}
                >
                  {r.payout_reference || '—'}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                  No rows match this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


export function MyRoyaltiesDialog({ open, onOpenChange }) {
  const { format } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byBook, setByBook] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [recent, setRecent] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [payouts, setPayouts] = useState(null);      // iter55g34
  const [statusFilter, setStatusFilter] = useState('all'); // 'all'|'paid_direct'|'paid_manual'|'pending'

  const reload = async () => {
    setLoading(true);
    try {
      const [s, b, m, r, t, p] = await Promise.all([
        royaltiesApi.summary(),
        royaltiesApi.byBook(),
        royaltiesApi.monthly(),
        royaltiesApi.recent(50),
        royaltiesApi.templatesBreakdown(),
        royaltiesApi.payoutsLedger(200).catch(() => ({ rows: [], totals: {} })),
      ]);
      setSummary(s);
      setByBook(b.by_book || []);
      setMonthly(m.monthly || []);
      setRecent(r.recent || []);
      setTemplates(t);
      setPayouts(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load royalties.');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) reload(); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="royalties-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            My royalties
          </DialogTitle>
          <DialogDescription>
            Every paid bookstore sale of your work credits your ledger at the royalty share you set when you published. Admin processes payouts monthly.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex h-48 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stat tiles */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatTile label="Lifetime"      icon={TrendingUp} value={format(summary?.lifetime_cents)}
                         sub={`${summary?.lifetime_sales || 0} sales`} />
              <StatTile label="This month"    icon={Calendar} value={format(summary?.this_month_cents)}
                         sub={`${summary?.this_month_sales || 0} sales · ${summary?.current_period}`} />
              <StatTile label="Pending payout" icon={Wallet} value={format(summary?.pending_payout_cents)}
                         sub={`${summary?.pending_sales || 0} sales awaiting payout`} />
              <StatTile label="Paid to date"  icon={BookOpen} value={format(summary?.paid_to_date_cents)} />
            </div>

            {/* iter55g34 — Recent payouts inline expander. */}
            {(payouts?.rows?.length || 0) > 0 && (
              <details className="rounded-md border border-border bg-card" data-testid="recent-payouts-details">
                <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[12px] font-semibold text-foreground">
                  <Wallet className="h-3.5 w-3.5 text-primary" />
                  Recent payouts
                  <span className="ml-2 text-[10.5px] text-muted-foreground">
                    {payouts.rows.length} row{payouts.rows.length === 1 ? '' : 's'} · direct {format(payouts.totals?.paid_direct_cents || 0)}
                    · pending {format(payouts.totals?.pending_cents || 0)}
                  </span>
                </summary>
                <div className="border-t border-border p-3">
                  <Table className="text-[11.5px]">
                    <TableHeader className="text-[10.5px] text-muted-foreground">
                      <TableRow className="text-left">
                        <TableHead className="pb-1.5 h-auto">Sold</TableHead>
                        <TableHead className="pb-1.5 h-auto">Book</TableHead>
                        <TableHead className="pb-1.5 h-auto text-right">Share</TableHead>
                        <TableHead className="pb-1.5 h-auto">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payouts.rows.slice(0, 5).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="py-1.5 font-mono-stat text-[10.5px]">{fmtDate(r.sold_at)}</TableCell>
                          <TableCell className="py-1.5 truncate max-w-[14rem]">{r.book_title}</TableCell>
                          <TableCell className="py-1.5 text-right font-mono-stat font-semibold text-primary">{format(r.author_share_cents)}</TableCell>
                          <TableCell className="py-1.5"><StatusPill status={r.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            )}

            <Tabs defaultValue="books" onValueChange={(v) => {
              // iter55g34 — First-time contextual tour when the writer
              // opens the Payouts tab. No-op after the first visit.
              if (v === 'payouts') {
                setTimeout(() => {
                  import('@/lib/tours').then(({ runTour }) => runTour('payouts-first-open'));
                }, 300);
              }
            }}>
              <TabsList>
                <TabsTrigger value="books"     data-testid="royalty-tab-books">By book</TabsTrigger>
                <TabsTrigger value="templates" data-testid="royalty-tab-templates">Templates</TabsTrigger>
                <TabsTrigger value="payouts"   data-testid="royalty-tab-payouts">Payouts</TabsTrigger>
                <TabsTrigger value="monthly"   data-testid="royalty-tab-monthly">Monthly trend</TabsTrigger>
                <TabsTrigger value="itemised"  data-testid="royalty-tab-itemised">Itemised</TabsTrigger>
                <TabsTrigger value="recent"    data-testid="royalty-tab-recent">Recent sales</TabsTrigger>
              </TabsList>

              <TabsContent value="books" className="mt-3">
                {byBook.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
                    No bookstore sales yet. Once a customer buys a copy you&apos;ll see your earnings here.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-md border border-border">
                    <Table className="text-[12px]">
                      <TableHeader className="bg-panel-header/60 text-[11px] text-muted-foreground">
                        <TableRow>
                          <TableHead className="px-3 py-2 h-auto">Book</TableHead>
                          <TableHead className="px-3 py-2 h-auto text-right">Sales</TableHead>
                          <TableHead className="px-3 py-2 h-auto text-right">Gross</TableHead>
                          <TableHead className="px-3 py-2 h-auto text-right">Royalty</TableHead>
                          <TableHead className="px-3 py-2 h-auto">Last sold</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {byBook.map((b) => (
                          <TableRow key={b.book_id} data-testid={`royalty-book-row-${b.book_id}`}>
                            <TableCell className="px-3 py-2 font-semibold">{b.book_title}</TableCell>
                            <TableCell className="px-3 py-2 text-right font-mono-stat">{b.sales}</TableCell>
                            <TableCell className="px-3 py-2 text-right font-mono-stat text-muted-foreground">{format(b.gross_cents)}</TableCell>
                            <TableCell className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{format(b.royalty_cents)}</TableCell>
                            <TableCell className="px-3 py-2 text-muted-foreground">{fmtDate(b.last_sold_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="monthly" className="mt-3">
                <MonthlyBarChart data={monthly} />
              </TabsContent>

              <TabsContent value="itemised" className="mt-3" data-testid="royalty-itemised-pane">
                <ItemisedRoyaltyPane />
              </TabsContent>

              <TabsContent value="templates" className="mt-3" data-testid="royalty-templates-pane">
                <TemplatesBreakdownPane data={templates} format={format} />
              </TabsContent>

              <TabsContent value="payouts" className="mt-3" data-testid="royalty-payouts-pane">
                <PayoutsPane data={payouts}
                              format={format}
                              statusFilter={statusFilter}
                              setStatusFilter={setStatusFilter}
                              csvUrl={royaltiesApi.payoutsLedgerCsvUrl()} />
              </TabsContent>

              <TabsContent value="recent" className="mt-3">
                {recent.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
                    No sales recorded yet.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-md border border-border">
                    <Table className="text-[12px]">
                      <TableHeader className="bg-panel-header/60 text-[11px] text-muted-foreground">
                        <TableRow>
                          <TableHead className="px-3 py-2 h-auto">Sold</TableHead>
                          <TableHead className="px-3 py-2 h-auto">Book</TableHead>
                          <TableHead className="px-3 py-2 h-auto">Kind</TableHead>
                          <TableHead className="px-3 py-2 h-auto text-right">Gross</TableHead>
                          <TableHead className="px-3 py-2 h-auto text-right">Rate</TableHead>
                          <TableHead className="px-3 py-2 h-auto text-right">Royalty</TableHead>
                          <TableHead className="px-3 py-2 h-auto">Payout</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recent.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="px-3 py-2 font-mono-stat">{fmtDate(r.sold_at)}</TableCell>
                            <TableCell className="px-3 py-2">{r.book_title}</TableCell>
                            <TableCell className="px-3 py-2 text-muted-foreground capitalize">{r.kind.replace('_', ' ')}</TableCell>
                            <TableCell className="px-3 py-2 text-right font-mono-stat text-muted-foreground">{format(r.gross_cents)}</TableCell>
                            <TableCell className="px-3 py-2 text-right font-mono-stat">{r.royalty_pct}%</TableCell>
                            <TableCell className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{format(r.royalty_cents)}</TableCell>
                            <TableCell className="px-3 py-2">
                              {r.payout_id
                                ? <span className="rounded-sm bg-emerald-100 text-emerald-900 px-1.5 py-0.5 text-[10.5px] font-semibold">paid</span>
                                : <span className="text-muted-foreground">pending</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
